import { ContactNumberLinks } from './contact-number-links.js';
import { useQuery } from '@tanstack/react-query';
import { defaultPortalPage } from '@hue/contracts';
import { Button, Card, CardHeader, Combobox } from '@hue/design-system';
import { Building2, MapPin, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { portalApi, type PortalCompany } from '../lib/portal-api.js';
import { portalServiceLabel } from '../lib/portal-service-terms.js';
import { useColorMode } from './brand.js';
import { SocialLinks } from './social-links.js';
import { PortalUnitMap } from './portal-unit-map.js';
import { EmptyCard, FilterBar, Pagination } from './data-view.js';
import { centeredFrame } from './image-frame.js';
import { FramedImage } from './image-framer.js';
import './public-portal.css';

export function PortalCompanyPage({
  company,
  onBook,
  preview = false,
  mode: previewMode,
}: {
  company: PortalCompany;
  onBook: (unitId: string, serviceId: string) => void;
  preview?: boolean;
  mode?: 'light' | 'dark';
}) {
  const mode = useColorMode();
  const page = company.page ?? defaultPortalPage;
  const [unitId, setUnitId] = useState(company.units.length === 1 ? company.units[0]!.id : '');
  const [all, setAll] = useState(false);
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const unit = company.units.find((u) => u.id === unitId) ?? company.units[0];
  const chosen = page.highlights.find((h) => h.unitId === unit?.id)?.serviceIds ?? [];
  const normalize = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/gu, '')
      .toLocaleLowerCase('pt-BR');
  const catalog = useQuery({
    queryKey: ['public', 'portal', 'services', company.key, unit?.id, search, offset],
    queryFn: () =>
      portalApi.companyServices(company.key, { unitId: unit!.id, search, limit: 12, offset }),
    enabled: all && !preview && Boolean(unit),
    retry: false,
  });
  const filtered = company.services.filter((s) =>
    normalize(s.displayName).includes(normalize(search)),
  );
  const listed = all
    ? preview
      ? filtered.slice(offset, offset + 12)
      : (catalog.data?.services ?? [])
    : chosen.flatMap((id) => {
        const s = company.services.find((s) => s.id === id);
        return s ? [s] : [];
      });
  const total = all ? (preview ? filtered.length : (catalog.data?.total ?? 0)) : listed.length;
  const image = (id: string, logo = false) => portalApi.mediaUrl(company.key, id, preview, logo);
  const logo =
    (previewMode ?? mode) === 'dark'
      ? (company.logoIds?.dark ?? company.logoIds?.main)
      : company.logoIds?.main;
  const symbol = company.logoIds?.symbol;
  const title = page.title || company.displayName;
  // Molduras com proporção fixa em todos os modelos: a medida ideal informada no editor vale sempre.
  const cover = page.coverId ? (
    <FramedImage
      src={image(page.coverId)}
      alt={`Espaço de ${company.displayName}`}
      frame={{ x: page.coverPositionX, y: page.coverPosition, zoom: page.coverZoom }}
      className="aspect-[2/1] w-full rounded-xl"
    />
  ) : null;
  const chooseUnit = (id: string, catalog = false) => {
    setUnitId(id);
    setAll(catalog);
    setSearch('');
    setOffset(0);
  };
  const services = (
    <section
      className="portal-services min-w-0"
      data-catalog={all}
      aria-label="Serviços da unidade"
    >
      {company.units.length > 1 ? (
        <div className="mb-6">
          <Combobox
            placeholder="Escolha uma opção"
            label="Unidade dos serviços"
            selectedLabel={unit?.displayName ?? ''}
            options={company.units.map((u) => ({ value: u.id, label: u.displayName }))}
            onSelect={(choice) => {
              if (choice.kind === 'option') chooseUnit(choice.value, all);
            }}
          />
        </div>
      ) : null}
      <div className="portal-services-heading mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold">
          {all ? 'Todos os serviços' : 'Destaques'}
          {unit ? ` · ${unit.displayName}` : ''}
        </h2>
        {all ? (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setAll(false);
              setOffset(0);
            }}
          >
            Voltar aos destaques
          </Button>
        ) : null}
      </div>
      {all ? (
        <FilterBar
          searchId="portal-service-search"
          searchLabel="Buscar serviço"
          searchPlaceholder="Nome do serviço"
          search={search}
          onSearchChange={(value) => {
            setSearch(value);
            setOffset(0);
          }}
          chips={[]}
          onClear={() => {
            setSearch('');
            setOffset(0);
          }}
          count={`${total} ${total === 1 ? 'serviço' : 'serviços'}`}
          resultsLabel={`Ver ${total} serviços`}
        >
          {null}
        </FilterBar>
      ) : (
        <p className="mb-3 text-sm text-content-muted">
          Selecionados pela empresa para esta unidade.
        </p>
      )}
      {all && !preview && catalog.isPending ? (
        <p role="status" className="py-6 text-sm text-content-muted">
          Consultando serviços…
        </p>
      ) : all && !preview && catalog.isError ? (
        <p role="alert" className="py-6 text-sm text-danger">
          Não foi possível consultar os serviços. Tente novamente.
        </p>
      ) : !listed.length ? (
        <EmptyCard
          title={all ? 'Nenhum serviço encontrado' : 'Nenhum destaque selecionado'}
          description={
            all ? 'Tente outro nome.' : 'Consulte todos os serviços publicados desta unidade.'
          }
          icon={Sparkles}
        />
      ) : (
        <div className="portal-service-list divide-y divide-surface-border">
          {listed.map((service) => (
            <div
              key={service.id}
              className="portal-service-item flex flex-wrap items-center justify-between gap-3 py-5"
            >
              <div className="min-w-0">
                <h3 className="font-semibold">{service.displayName}</h3>
                <p className="mt-1 text-sm text-content-muted">
                  {portalServiceLabel(service, unit?.id)}
                </p>
                {all && service.description ? (
                  <p className="mt-2 max-w-prose text-sm text-content-muted">
                    {service.description}
                  </p>
                ) : null}
              </div>
              <Button type="button" onClick={() => unit && onBook(unit.id, service.id)}>
                Ver horários
              </Button>
            </div>
          ))}
        </div>
      )}
      {all ? (
        <Pagination
          total={total}
          offset={offset}
          limit={12}
          onOffsetChange={setOffset}
          itemLabel={(count) => (count === 1 ? 'serviço' : 'serviços')}
        />
      ) : (
        <Button
          type="button"
          className="mt-4"
          variant="outline"
          onClick={() => {
            setAll(true);
            setOffset(0);
          }}
        >
          Ver todos os {company.services.length} serviços
        </Button>
      )}
    </section>
  );
  const identity = (
    <>
      <div className="portal-company-identity flex items-center gap-4">
        {/* O Símbolo ocupa o mesmo quadrado da inicial, com o mesmo arredondamento. */}
        {symbol ? (
          <img
            src={image(symbol, true)}
            alt=""
            className="portal-company-initial size-14 shrink-0 rounded-xl object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="portal-company-initial grid size-14 shrink-0 place-items-center rounded-xl bg-brand-500 text-2xl font-semibold text-on-brand"
          >
            {title.slice(0, 1)}
          </span>
        )}
        {page.nameDisplay === 'logo' && logo ? (
          <h1 className="min-w-0">
            <img
              src={image(logo, true)}
              alt={title}
              className="portal-company-logo max-h-20 max-w-full object-contain"
            />
          </h1>
        ) : (
          <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
        )}
      </div>
      {page.headline ? (
        <p className="portal-company-headline mt-5 text-xl font-semibold">{page.headline}</p>
      ) : null}
      <p className="mt-4 max-w-prose whitespace-pre-line text-sm leading-7 text-content-secondary">
        {company.description}
      </p>
      <ContactNumberLinks numbers={company.contactNumbers} />
      {page.showSocialLinks ? (
        <SocialLinks links={company.socialLinks} label="Redes sociais da empresa" />
      ) : null}
    </>
  );
  const location = unit ? (
    <Card className="portal-location min-w-0">
      <CardHeader title="Onde encontrar" icon={<MapPin className="size-4" />} />
      <UnitContacts unit={unit} showSocialLinks={page.showSocialLinks} />
    </Card>
  ) : null;
  const gallery = page.galleryIds.length ? (
    <section className="portal-gallery mt-8">
      <h2 className="text-xl font-semibold">Nosso espaço</h2>
      <div className="portal-gallery-grid mt-4 grid gap-4">
        {page.galleryIds.map((id) => (
          <FramedImage
            key={id}
            src={image(id)}
            alt={`Ambiente de ${company.displayName}`}
            frame={page.galleryFrames.find((frame) => frame.id === id) ?? centeredFrame}
            className="aspect-[4/3] w-full rounded-xl"
          />
        ))}
      </div>
    </section>
  ) : null;
  const unitIntro = unit ? (
    <Card className="portal-unit-intro mb-8 min-w-0">
      <CardHeader title={unit.displayName} icon={<Building2 className="size-4" />} />
      <p className="mb-4 text-sm font-semibold text-content-secondary">
        Seu atendimento nesta unidade
      </p>
      <div className="portal-unit-details">
        {cover}
        <UnitContacts unit={unit} showName={false} showSocialLinks={page.showSocialLinks} />
      </div>
      {company.units.length > 1 ? (
        <Button
          type="button"
          className="mt-5"
          variant="outline"
          onClick={() => {
            setUnitId('');
            setAll(false);
          }}
        >
          Trocar unidade
        </Button>
      ) : null}
    </Card>
  ) : null;
  return (
    <section
      className="portal-company-page hue-palette-scope rounded-2xl bg-surface p-5 text-content sm:p-8"
      data-palette={page.palette}
      data-mode={previewMode ?? mode}
      data-layout={page.layout}
    >
      {page.layout === 'essential' ? (
        <>
          <header className="portal-essential-intro">{identity}</header>
          <div className="mt-8">{services}</div>
          <div className="mt-8">{location}</div>
        </>
      ) : page.layout === 'visual' ? (
        <>
          <header className="portal-visual-intro">
            <div className="portal-visual-cover">
              {cover ?? (
                <div className="portal-cover-placeholder">
                  <Building2 className="size-12" aria-hidden="true" />
                  <span>{company.displayName}</span>
                </div>
              )}
            </div>
            <div className="portal-visual-identity">{identity}</div>
          </header>
          <div className="mt-8">{services}</div>
          {gallery}
          <div className="mt-8">{location}</div>
        </>
      ) : page.layout === 'units' ? (
        <>
          <header className="portal-units-intro">{identity}</header>
          <div className="mt-8">
            {company.units.length > 1 && !unitId ? (
              <section>
                <h2 className="text-xl font-semibold">Escolha onde ser atendido</h2>
                <p className="mt-2 text-sm text-content-muted">
                  Conheça cada unidade e seus serviços em destaque.
                </p>
                <div className="portal-unit-grid mt-5 grid gap-5">
                  {company.units.map((u) => (
                    <Card key={u.id} className="min-w-0">
                      <CardHeader title={u.displayName} icon={<Building2 className="size-4" />} />
                      {cover}
                      <div className="my-5">
                        <UnitContacts
                          unit={u}
                          showName={false}
                          showSocialLinks={page.showSocialLinks}
                        />
                      </div>
                      <h3 className="text-sm font-semibold">Serviços em destaque</h3>
                      <ul className="my-3 space-y-2 text-sm text-content-muted">
                        {(page.highlights.find((h) => h.unitId === u.id)?.serviceIds ?? []).flatMap(
                          (id) => {
                            const service = company.services.find((s) => s.id === id);
                            return service ? [<li key={id}>{service.displayName}</li>] : [];
                          },
                        )}
                      </ul>
                      <div className="flex flex-wrap gap-3">
                        <Button type="button" onClick={() => chooseUnit(u.id)}>
                          Conhecer esta unidade
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => chooseUnit(u.id, true)}
                        >
                          Ver todos os {company.services.length} serviços
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            ) : (
              <>
                {unitIntro}
                {services}
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="portal-showcase-grid">
            <div className="portal-showcase-story">
              <header>{identity}</header>
              {cover ? <div className="mt-8">{cover}</div> : null}
              <div className="mt-8">{location}</div>
            </div>
            <div className="portal-showcase-services">{services}</div>
          </div>
          {gallery}
        </>
      )}
    </section>
  );
}

function UnitContacts({
  unit,
  showName = true,
  showSocialLinks = false,
}: {
  unit: PortalCompany['units'][number];
  showName?: boolean;
  showSocialLinks?: boolean;
}) {
  const address = [unit.street, unit.streetNumber, unit.district, unit.city, unit.state]
    .filter(Boolean)
    .join(', ');
  return (
    <div className="portal-unit-contacts">
      {showName ? <h3 className="font-semibold">{unit.displayName}</h3> : null}
      <p className="mt-3 text-sm leading-6 text-content-muted">
        {address || 'Consulte a localização com a empresa.'}
      </p>
      {/* Sem rua e cidade, o mapa apontaria para um lugar genérico; melhor não oferecer. */}
      {unit.street && unit.city ? (
        <div className="mt-3">
          <PortalUnitMap unitName={unit.displayName} address={address} />
        </div>
      ) : null}
      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
        <ContactNumberLinks
          numbers={
            unit.contactNumbers ??
            (unit.phone
              ? [{ label: 'Atendimento', number: unit.phone, channels: ['CALL'], isPublic: true }]
              : [])
          }
        />
        {unit.email ? (
          <a
            className="break-all text-sm text-content underline underline-offset-4"
            href={`mailto:${unit.email}`}
          >
            {unit.email}
          </a>
        ) : null}
      </div>
      {showSocialLinks ? (
        <SocialLinks links={unit.socialLinks} label={`Redes sociais de ${unit.displayName}`} />
      ) : null}
    </div>
  );
}
